import React, { ChangeEvent, useEffect, useState } from 'react';
import { IoMdClose } from 'react-icons/io';
import SearchSelect from '../components/UI/search_select/search_select';
import { MainInfoProps } from '../containers/client/client-main-info';
import { addClient, getClientApi, searchClients } from '../api/client';
import { ValidationError } from '@quipa/api';
import { FormValidationError } from '../ui_components/validation/validationForm';
import { addProject } from '../api/projects';
import { ErrorInputPopUp } from '../components/input_popups/error_input_popup';
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";

type QuickAddClientProps = {
  active: boolean;
  onCancel(): void;
  handleClearKeyword?(): void;
  accountId: number;
  client: any;
  getRooms(clientId: number): void;
  onClientSaved(client: any): void;
  getClient(): void;
};

const QuickAddClient = (props: QuickAddClientProps) => {
  const {
    active,
    onCancel,
    handleClearKeyword,
    accountId,
    client,
    getRooms,
    onClientSaved,
    getClient,
  } = props;

  const [addClientError, setAddClientError] = useState<any>({
    message: '',
    error: false,
  });
  const [isDisplaySuggestionsOpen, setIsDisplaySuggestionsOpen] = useState<boolean>(false);
  const [displaySuggestions, setDisplaySuggestions] = useState<string[]>([]);
  const [depthErrors, setDepthErrors] = useState<string[]>([]);
  const [parentClient, setParentClient] = useState<any>({
    searchKeyword: '',
    parentClientName: '',
    parentClientId: 0,
    parentClients: 0,
  });
  const [subClientOfCheck, setSubClientOfCheck] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<any>({
    displayName: false,
    billingType: false,
    searchField: false,
  });
  const [form, setForm] = useState<any>({
    id: undefined,
    active: true,
    additionalContactDetails: [],
    avatarId: '',
    billToParent: false,
    displayName: '',
    billingDetails: {
      additionalPriceAdjustment: 'none',
      additionalPriceAdjustmentValue: '',
      billingType: '',
      cellPhone: '',
      cityTown: '',
      company: '',
      country: '',
      email: '',
      fax: '',
      firstName: '',
      homePhone: '',
      lastName: '',
      officePhone: '',
      stateProvince: '',
      streetAddress: '',
      unitSuite: '',
      website: '',
      zipCode: '',
    },
    billingDetailsDirectly: {
      additionalPriceAdjustment: 'none',
      additionalPriceAdjustmentValue: '',
      billingType: '',
      cellPhone: '',
      cityTown: '',
      company: '',
      country: '',
      email: '',
      fax: '',
      firstName: '',
      homePhone: '',
      lastName: '',
      officePhone: '',
      stateProvince: '',
      streetAddress: '',
      unitSuite: '',
      website: '',
      zipCode: '',
    },
    billingSalesTax: '',
    billingType: '',
    cellPhone: '',
    cityTown: '',
    company: '',
    country: '',
    customDisplayName: false,
    email: '',
    fax: '',
    firstName: '',
    homePhone: '',
    lastName: '',
    notes: '',
    officePhone: '',
    parentClientId: 0,
    projectManager: '',
    stateProvince: '',
    streetAddress: '',
    unitSuite: '',
    website: '',
    zipCode: '',
  });
  const releaseDate = new Date('2026-01-23');
  const thirtyDaysAfterRelease = new Date(releaseDate);
  thirtyDaysAfterRelease.setDate(thirtyDaysAfterRelease.getDate() + 30);
  const showClientPriceAdjustmentBadge = thirtyDaysAfterRelease > new Date();

  const handleResetAddClientError = () => {
    setAddClientError((prev: any) => ({ ...prev, error: false, message: '' }));
  };

  const handleChangeInputs = (e: any) => {
    const { name, value } = e.target;

    setForm((prev: any) => {
      const new_obj = prev;
      const new_obj_billing_key = prev.billToParent ? 'billingDetails' : 'billingDetailsDirectly';
      const new_obj_billing = prev.billToParent ? prev.billingDetails : prev.billingDetailsDirectly;

      new_obj[name] = value;

      // set display name while setting first name and last name
      if (name === 'firstName' || name === 'lastName') {
        const pre_fill_first = new_obj.firstName ? new_obj.firstName.trim() : new_obj.firstName;
        const pre_fill_last = new_obj.lastName ? new_obj.lastName.trim() : new_obj.lastName;

        if (!pre_fill_first && !pre_fill_last) {
          new_obj['displayName'] = new_obj.company ? new_obj.company.trim() : new_obj.company;
        } else {
          new_obj['displayName'] = `${pre_fill_last}${
            pre_fill_last && pre_fill_first && ', '
          }${pre_fill_first}`;
        }
      }

      if (name === 'displayName') {
        new_obj['customDisplayName'] = true;
      }

      // set company name as display name if there is no first name and last name
      if (name === 'company' && !new_obj.firstName && !new_obj.lastName) {
        new_obj['displayName'] = new_obj.company ? new_obj.company.trim() : new_obj.company;
      }

      if (
        !form.billToParent &&
        (name === 'firstName' || name === 'lastName' || name === 'company')
      ) {
        new_obj_billing[name] = value;
      }

      return {
        ...prev,
        ...new_obj,
        [new_obj_billing_key]: { ...new_obj_billing },
      };
    });

    setFormErrors((prev: any) => {
      if (name === 'company' || name === 'firstName' || name === 'lastName') {
        return {
          ...prev,
          displayName: false,
        };
      }
      return {
        ...prev,
        [name]: !value,
      };
    });

    handleResetAddClientError();
  };

  useEffect(() => {
    let suggestions: string[] = [];
    const sugg_first_name = form.firstName && form.firstName.trim();
    const sugg_last_name = form.lastName && form.lastName.trim();
    const sugg_company = form.company && form.company.trim();

    if (sugg_last_name) {
      let sub_sugg = sugg_last_name;
      if (sugg_first_name) {
        sub_sugg += `, ${sugg_first_name}`;
      }

      suggestions.push(sub_sugg);
    }

    if (sugg_first_name) {
      let sub_sugg = sugg_first_name;
      if (sugg_last_name) {
        sub_sugg += ` ${sugg_last_name}`;
      }

      suggestions.push(sub_sugg);
    }

    if (sugg_company) {
      suggestions.push(sugg_company);
    }

    setDisplaySuggestions(suggestions);
  }, [form.firstName, form.lastName, form.company]);

  const handleSetDisplayNameDropdown = (val: string) => {
    setForm((prev: any) => {
      return {
        ...prev,
        displayName: val,
      };
    });
  };

  const handleChangeRadio = (val: string) => {
    setForm((prev: any) => {
      const new_obj_billing_key = prev.billToParent ? 'billingDetails' : 'billingDetailsDirectly';
      const new_obj_billing = prev.billToParent ? prev.billingDetails : prev.billingDetailsDirectly;

      new_obj_billing['billingType'] = val;
      return {
        ...prev,
        billingType: val,
        [new_obj_billing_key]: { ...new_obj_billing },
      };
    });

    setFormErrors((prev: any) => {
      return {
        ...prev,
        billingType: false,
      };
    });
  };

  const handleChangeBillToRadio = (val: boolean) => {
    setForm((prev: any) => {
      const new_obj_billing_key = 'billingDetailsDirectly';
      let new_obj_billing = prev.billingDetailsDirectly;

      if (!val) {
        new_obj_billing.firstName = prev.firstName;
        new_obj_billing.lastName = prev.lastName;
        new_obj_billing.company = prev.company;
        new_obj_billing.billingType = prev.billingType;
      }

      return {
        ...prev,
        billToParent: val || false,
        [new_obj_billing_key]: { ...new_obj_billing },
      };
    });
  };

  const handleOpenSearchClients = (e: any) => {
    const { checked } = e.target;

    setSubClientOfCheck(checked);

    if (!checked) {
      setParentClient({
        searchKeyword: '',
        parentClientName: '',
        parentClientId: 0,
        parentClients: 0,
      });
    }
  };

  const handleCancel = () => {
    setForm({
      id: undefined,
      active: true,
      additionalContactDetails: [],
      avatarId: '',
      billToParent: false,
      displayName: '',
      billingDetails: {
        additionalPriceAdjustment: 'none',
        additionalPriceAdjustmentValue: '',
        billingType: '',
        cellPhone: '',
        cityTown: '',
        company: '',
        country: '',
        email: '',
        fax: '',
        firstName: '',
        homePhone: '',
        lastName: '',
        officePhone: '',
        stateProvince: '',
        streetAddress: '',
        unitSuite: '',
        website: '',
        zipCode: '',
      },
      billingDetailsDirectly: {
        additionalPriceAdjustment: 'none',
        additionalPriceAdjustmentValue: '',
        billingType: '',
        cellPhone: '',
        cityTown: '',
        company: '',
        country: '',
        email: '',
        fax: '',
        firstName: '',
        homePhone: '',
        lastName: '',
        officePhone: '',
        stateProvince: '',
        streetAddress: '',
        unitSuite: '',
        website: '',
        zipCode: '',
      },
      billingSalesTax: '',
      billingType: '',
      cellPhone: '',
      cityTown: '',
      company: '',
      country: '',
      customDisplayName: false,
      email: '',
      fax: '',
      firstName: '',
      homePhone: '',
      lastName: '',
      notes: '',
      officePhone: '',
      parentClientId: 0,
      projectManager: '',
      stateProvince: '',
      streetAddress: '',
      unitSuite: '',
      website: '',
      zipCode: '',
    });

    setFormErrors({
      displayName: false,
      billingType: false,
      searchField: false,
    });

    setParentClient({
      searchKeyword: '',
      parentClientName: '',
      parentClientId: 0,
      parentClients: 0,
    });

    setSubClientOfCheck(false);
    setIsDisplaySuggestionsOpen(false);
    setDisplaySuggestions([]);
    handleResetAddClientError();

    onCancel();
  };

  const handleAdditionalPriceAdjustment = (val: string, name: string) => {
    setForm((prev: any) => {
      const temp_obj = prev.billToParent ? prev.billingDetails : prev.billingDetailsDirectly;
      const temp_obj_key = prev.billToParent ? 'billingDetails' : 'billingDetailsDirectly';
      return {
        ...prev,
        [temp_obj_key]: {
          ...temp_obj,
          [name]: val,
          additionalPriceAdjustmentValue:
            val === 'none' ? '' : temp_obj?.additionalPriceAdjustmentValue || '',
        },
      };
    });
  };

  const handleAdditionalPriceAdjustmentValue = (e: ChangeEvent<HTMLInputElement>) => {
    let { name, value } = e.target;
    const decimalNumbersRegex = /^$|^(0|[1-9]\d*)(\.\d{0,2})?$/;

    if (value && !decimalNumbersRegex.test(value)) return;

    setForm((prev: any) => {
      const temp_obj = prev.billToParent ? prev.billingDetails : prev.billingDetailsDirectly;
      const temp_obj_key = prev.billToParent ? 'billingDetails' : 'billingDetailsDirectly';
      return {
        ...prev,
        [temp_obj_key]: {
          ...temp_obj,
          [name]: value,
        },
      };
    });
  };

  const handleAdditionalPriceAdjustmentValueBlur = (e: ChangeEvent<HTMLInputElement>) => {
    let { name, value } = e.target;

    if (value) {
      if (value.endsWith('.')) {
        value += '00';
      } else if (value?.split('.')?.[1]?.length === 1) {
        value += '0';
      } else if (!value.includes('.')) {
        value += '.00';
      }

      setForm((prev: any) => {
        const temp_obj = prev.billToParent ? prev.billingDetails : prev.billingDetailsDirectly;
        const temp_obj_key = prev.billToParent ? 'billingDetails' : 'billingDetailsDirectly';
        return {
          ...prev,
          [temp_obj_key]: {
            ...temp_obj,
            [name]: value,
          },
        };
      });
    }
  };

  const getMaxDepthIterative = (rootClient: any): number => {
    let maxDepth = 0;
    const stack: { client: any; depth: number }[] = [{ client: rootClient, depth: 1 }];
    if (rootClient) {
      while (stack.length > 0) {
        const { client, depth } = stack.pop()!;
        maxDepth = Math.max(maxDepth, depth);
        for (const subClient of client.subClients) {
          stack.push({ client: subClient, depth: depth + 1 });
        }
      }
    }
    return maxDepth;
  };

  const handleSelectClient = async (id: number, name: string) => {
    const updatedForm = {
      ...form,
      billToParent: id ? true : false,
    };
    updatedForm.parentClientId = id;
    let res: any;
    let res2: any;

    if (updatedForm.billToParent) {
      res = await getClientApi({ clientId: id, accountId });
      if (client) res2 = await getClientApi({ clientId: client.id, accountId });

      if (!res.error) {
        const parent = res;
        const obj_to_update = parent.billToParent
          ? { ...updatedForm.billingDetails, ...parent.billingDetails }
          : parent.billingDetailsDirectly
          ? { ...updatedForm.billingDetailsDirectly, ...parent.billingDetailsDirectly }
          : {};

        updatedForm.billingDetails = obj_to_update;
        updatedForm.billingType = obj_to_update.billingType;
        updatedForm.billingDetails['additionalPriceAdjustment'] =
          obj_to_update.additionalPriceAdjustment || 'none';
        updatedForm.billingDetails['additionalPriceAdjustmentValue'] =
          obj_to_update.additionalPriceAdjustmentValue || '';
      }
    }

    setForm((prev: any) => {
      return {
        ...prev,
        ...updatedForm,
      };
    });

    setParentClient((prev: any) => {
      return {
        ...prev,
        parentClientName: name,
        parentClientId: id,
        isParentChanged: id !== prev.parentClientId,
        parentClients: id
          ? !client
            ? res
              ? res.parentClients.length + 1
              : []
            : getMaxDepthIterative(res2) + res.parentClients.length
          : 0,
      };
    });

    setFormErrors((prev: any) => {
      return {
        ...prev,
        searchField: false,
        billingType: false,
      };
    });
  };

  const handleSearch: MainInfoProps['handleSearch'] = async (text: string) => {
    const res = await searchClients({
      text,
      accountId,
      excludeId: client ? client.id : undefined,
    });
    if (res.error) {
      return [];
    }
    return res
      .filter((item: any) =>
        client
          ? // if we have a `client` to exclude, drop any item whose parent.id === client.id
            !(item.parent && item.parent.id === client.id)
          : true
      )
      .map((r: any) => ({
        id: r.id,
        displayName: r.displayName,
        parentName: r.parent && r.parent.displayName, // undefined if no parent
      }));
  };

  const handleOpenDisplayNameSuggestions = (val: boolean) => {
    setIsDisplaySuggestionsOpen(val);
  };

  const handleValidateOnBlur = (e: any) => {
    const { name, value } = e.target;

    handleResetAddClientError();
    handleOpenDisplayNameSuggestions(false);

    setFormErrors((prev: any) => {
      return {
        ...prev,
        [name]: !value,
      };
    });

    // if (displaySuggestions && displaySuggestions.length > 0 && !form.customDisplayName) {
    //   setForm((prev: any) => {
    //     return {
    //       ...prev,
    //       displayName: displaySuggestions[0],
    //     };
    //   });
    //   setFormErrors((prev: any) => {
    //     return {
    //       ...prev,
    //       displayName: false,
    //     };
    //   });
    // }
  };

  const handleValidateQuickAddForm = () => {
    let checkCase = false;
    let checkDisplayName = false;
    let checkBillingType = false;
    let checkSearchField = false;

    const case_billToParent = form.billToParent
      ? form.billingDetails && !form.billingDetails.billingType
      : form.billingDetailsDirectly && !form.billingDetailsDirectly.billingType;

    if (!form.displayName) {
      setFormErrors((prev: any) => ({ ...prev, displayName: true }));
      checkDisplayName = false;
    } else if (form.displayName.includes(':')) {
      setFormErrors((prev: any) => ({ ...prev, displayName: true }));
      setAddClientError({
        message: 'Display name cannot contain ":"',
        error: true,
      });
      checkDisplayName = false;
    } else {
      setFormErrors((prev: any) => ({ ...prev, displayName: false }));
      handleResetAddClientError();
      checkDisplayName = true;
    }

    if (!form.billingType || case_billToParent) {
      setFormErrors((prev: any) => ({ ...prev, billingType: true }));
      checkBillingType = false;
    } else {
      setFormErrors((prev: any) => ({ ...prev, billingType: false }));
      checkBillingType = true;
    }

    if (subClientOfCheck && !parentClient.parentClientId) {
      setFormErrors((prev: any) => ({ ...prev, searchField: true }));
      checkSearchField = false;
    } else {
      setFormErrors((prev: any) => ({ ...prev, searchField: false }));
      checkSearchField = true;
    }

    if (checkDisplayName && checkBillingType && checkSearchField) {
      checkCase = true;
    }

    return checkCase;
  };

  const handleAdd = async (e: any) => {
    e.preventDefault();
    const validation = handleValidateQuickAddForm();

    if (validation) {
      handleResetAddClientError();

      const payload = {
        ...form,
      };

      if (parentClient.parentClients <= 3) {
        const res = await addClient({
          client: { ...payload },
          accountId,
        });
        if (res.error) {
          setAddClientError({
            message:
              res.message ||
              res.data.message ||
              (res && res.displayName && res.displayName.replace('DisplayName', 'Display Name')) ||
              '',
            error: true,
          });
          if (res.name === 'ValidationError') {
            const errors = (res as Error as ValidationError).errors;
            if (errors.displayName) {
              errors.displayName = errors.displayName.replace('DisplayName', 'Display Name');
            }
            throw FormValidationError.fromErrorDictionary(errors);
          } else {
            throw new Error(res.message);
          }
        }
        handleResetAddClientError();

        if (res && res.id) {
          const payload_project = {
            clientId: res.id,
            accountId: accountId,
          };
          await addProject(payload_project);
        }

        let savedClient = res;
        if (res && res.id) {
          try {
            const refreshedClient = await getClientApi({ clientId: res.id, accountId });
            if (refreshedClient && !refreshedClient.error) {
              savedClient = refreshedClient;
            }
          } catch (error) {
            savedClient = res;
          }
        }

        onClientSaved(savedClient);

        if (getRooms) {
          getRooms(savedClient.id);
        }

        if (getClient) {
          await getClient();
        }

        handleCancel();
      } else {
        const depthErrors_array: string[] = [...depthErrors];
        const message =
          "In the client's organization system, a maximum of four levels are possible.";
        if (!depthErrors_array.includes(message)) {
          depthErrors_array.push(message);
        }
        setDepthErrors(depthErrors_array);
      }
    }
  };

  return (
    <>
      <div className={`quick_add_container ${active ? 'active' : ''}`}>
        <div className="quick_add_modal">
          <div className="quick_add_modal_header">
            <div className="quick_add_modal_header_title">
              <span>Quick Add - New Client</span>
            </div>

            <div>
              <button type="button" onClick={handleCancel}>
                <IoMdClose className="x_btn" />
              </button>
            </div>
          </div>

          <div className="quick_add_modal_form_container">
            <form className="quick_add_modal_form">
              <div className="quick_add_modal_form_row search">
                <div className="quick_add_modal_form_row_checkbox_container">
                  <div className="quick_add_modal_form_row_checkbox">
                    <input
                      checked={subClientOfCheck}
                      onChange={(e) => handleOpenSearchClients(e)}
                      id="sub_clients"
                      type="checkbox"
                      className="checkbox"
                    />
                  </div>

                  <label htmlFor="sub_clients">Sub-client of</label>
                </div>

                {subClientOfCheck && (
                  <div
                    className={`search_sub_clients_container ${
                      formErrors.searchField ? 'error' : ''
                    }`}
                  >
                    <SearchSelect
                      searchType="modal"
                      onItemClick={handleSelectClient}
                      onSearch={handleSearch}
                      initialValue={parentClient.parentClientName}
                      placeholder="Please Select..."
                      keepKeyword
                      onKeywordCleared={handleClearKeyword}
                      required={true}
                      searchItem="client"
                      searchPage="clients"
                    />
                  </div>
                )}
              </div>

              <div className="quick_add_modal_form_row">
                <div className="first_name">
                  <label htmlFor="quick_add_first_name">First Name</label>

                  <div>
                    <input
                      onChange={(e) => handleChangeInputs(e)}
                      name="firstName"
                      value={form.firstName}
                      className="input shadow"
                      id="quick_add_first_name"
                      type="text"
                      autoComplete="off"
                    />
                  </div>
                </div>

                <div className="last_name">
                  <label htmlFor="quick_add_last_name">Last Name</label>

                  <div>
                    <input
                      onChange={(e) => handleChangeInputs(e)}
                      name="lastName"
                      value={form.lastName}
                      className="input shadow"
                      id="quick_add_last_name"
                      type="text"
                      autoComplete="off"
                    />
                  </div>
                </div>
              </div>

              <div className="quick_add_modal_form_row">
                <div className="company_name">
                  <label htmlFor="quick_add_company_name">Company Name</label>

                  <div>
                    <input
                      onChange={(e) => handleChangeInputs(e)}
                      name="company"
                      value={form.company}
                      className="input shadow"
                      id="quick_add_company_name"
                      type="text"
                      autoComplete="off"
                    />
                  </div>
                </div>
              </div>

              <div className="quick_add_modal_form_row">
                <div className="display_name">
                  <label htmlFor="quick_add_display_name">
                    Display Name <span className="required">*</span>
                  </label>

                  <div style={{ position: 'relative' }}>
                    <input
                      onChange={(e) => handleChangeInputs(e)}
                      onBlur={(e) => handleValidateOnBlur(e)}
                      onFocus={() => handleOpenDisplayNameSuggestions(true)}
                      name="displayName"
                      value={form.displayName}
                      placeholder="Example: John’s NY Apartment"
                      className={`input shadow ${
                        formErrors.displayName || addClientError.error ? 'error' : ''
                      }`}
                      id="quick_add_display_name"
                      type="text"
                      autoComplete="off"
                      autoFocus={addClientError.error}
                    />

                    <ErrorInputPopUp
                      active={(addClientError && addClientError.error) || false}
                      message={(addClientError && addClientError.message) || ''}
                    />

                    {isDisplaySuggestionsOpen &&
                      displaySuggestions &&
                      displaySuggestions.length > 0 && (
                        <div className="quick_add_display_name_dropdown">
                          {displaySuggestions.map((sugg: any, index: number) => {
                            return (
                              <div
                                onMouseDown={() => {
                                  handleSetDisplayNameDropdown(sugg);
                                }}
                                className="quick_add_display_name_dropdown_row"
                                key={index}
                              >
                                <span>{sugg}</span>
                                {sugg === `${form.lastName}, ${form.firstName}` && (
                                  <span className="default_choice">Default</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                  </div>
                </div>
              </div>

              {subClientOfCheck && (
                <div className="quick_add_modal_form_row bill_to">
                  <label>Bill To</label>

                  <div className="quick_add_modal_form_row_bill_to">
                    <div className="quick_add_modal_form_row_bill_to_radio_row">
                      <label htmlFor="quick_add_bill_to_parent" className="radio_container">
                        <input
                          id="quick_add_bill_to_parent"
                          className="radio"
                          name="bill_to"
                          type="radio"
                          onChange={() => handleChangeBillToRadio(true)}
                          checked={form.billToParent}
                        />
                      </label>

                      <label htmlFor="quick_add_bill_to_parent">Parent client</label>
                    </div>

                    <div className="quick_add_modal_form_row_bill_to_radio_row">
                      <label htmlFor="quick_add_bill_to_directly" className="radio_container">
                        <input
                          id="quick_add_bill_to_directly"
                          className="radio"
                          name="bill_to"
                          type="radio"
                          onChange={() => handleChangeBillToRadio(false)}
                          checked={!form.billToParent}
                        />
                      </label>

                      <label htmlFor="quick_add_bill_to_directly">This client directly</label>
                    </div>
                  </div>
                </div>
              )}

              <div className="quick_add_modal_form_row billing_type">
                <label>
                  Billing Type <span className="required">*</span>
                </label>

                <div className="quick_add_modal_form_row_billing_type">
                  <div className="quick_add_modal_form_row_billing_type_radio_row">
                    <label
                      htmlFor="quick_add_retail"
                      className={`radio_container ${formErrors.billingType ? 'error' : ''}`}
                    >
                      <input
                        id="quick_add_retail"
                        className="radio"
                        name="billing_type"
                        type="radio"
                        onChange={() => handleChangeRadio('Retail')}
                        checked={
                          form.billToParent
                            ? form.billingDetails && form.billingDetails.billingType === 'Retail'
                            : form.billingDetailsDirectly &&
                              form.billingDetailsDirectly.billingType === 'Retail'
                        }
                      />
                    </label>

                    <label htmlFor="quick_add_retail">
                      Retail <span className="parentheses">(Homeowners)</span>
                    </label>
                  </div>

                  <div className="quick_add_modal_form_row_billing_type_radio_row">
                    <label
                      htmlFor="quick_add_trade"
                      className={`radio_container ${formErrors.billingType ? 'error' : ''}`}
                    >
                      <input
                        id="quick_add_trade"
                        className="radio"
                        name="billing_type"
                        type="radio"
                        onChange={() => handleChangeRadio('Trade')}
                        checked={
                          form.billToParent
                            ? form.billingDetails && form.billingDetails.billingType === 'Trade'
                            : form.billingDetailsDirectly &&
                              form.billingDetailsDirectly.billingType === 'Trade'
                        }
                      />
                    </label>

                    <label htmlFor="quick_add_trade">
                      Trade <span className="parentheses">(Designers or resellers)</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label className="text-[16px] font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    Additional Price Adjustment
                  </Label>
                  {showClientPriceAdjustmentBadge ? (
                    <span className="new_pill">
                      New
                    </span>
                  ) : null}
                </div>
                <div className="flex items-center gap-4">
                  <RadioGroup
                    className="flex gap-4"
                    value={
                      form.billToParent
                        ? form?.billingDetails?.additionalPriceAdjustment
                        : form?.billingDetailsDirectly?.additionalPriceAdjustment
                    }
                    onValueChange={(val) =>
                      handleAdditionalPriceAdjustment(val, 'additionalPriceAdjustment')
                    }
                    aria-readonly={false}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem
                        // disabled={loading || disabled}
                        aria-readonly={false}
                        value="none"
                        id="additional_price_none"
                      />
                      <Label
                        className="cursor-pointer leading-none text-[16px] peer-disabled:cursor-not-allowed peer-disabled:opacity-70 font-normal"
                        htmlFor="additional_price_none"
                      >
                        None
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem
                        // disabled={loading || disabled}
                        aria-readonly={false}
                        value="markup"
                        id="additional_price_markup"
                      />
                      <Label
                        className="cursor-pointer leading-none text-[16px] peer-disabled:cursor-not-allowed peer-disabled:opacity-70 font-normal"
                        htmlFor="additional_price_markup"
                      >
                        Markup
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem
                        // disabled={loading || disabled}
                        aria-readonly={false}
                        value="discount"
                        id="additional_price_discount"
                      />
                      <Label
                        className="cursor-pointer leading-none text-[16px] peer-disabled:cursor-not-allowed peer-disabled:opacity-70 font-normal"
                        htmlFor="additional_price_discount"
                      >
                        Discount
                      </Label>
                    </div>
                  </RadioGroup>

                  {form.billToParent ? (
                    form.billingDetails?.additionalPriceAdjustment !== 'none' ? (
                      <div className="flex items-center gap-2">
                        <Input
                          placeholder="0.00"
                          name="additionalPriceAdjustmentValue"
                          value={
                            form.billToParent
                              ? form.billingDetails?.additionalPriceAdjustmentValue || ''
                              : form.billingDetailsDirectly?.additionalPriceAdjustmentValue || ''
                          }
                          onChange={(e) => handleAdditionalPriceAdjustmentValue(e)}
                          onBlur={(e) => handleAdditionalPriceAdjustmentValueBlur(e)}
                          className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm w-20"
                        />
                        <span className="text-sm text-muted-foreground">%</span>
                      </div>
                    ) : null
                  ) : form.billingDetailsDirectly?.additionalPriceAdjustment !== 'none' ? (
                    <div className="flex items-center gap-2">
                      <Input
                        placeholder="0.00"
                        name="additionalPriceAdjustmentValue"
                        value={
                          form.billToParent
                            ? form.billingDetails?.additionalPriceAdjustmentValue || ''
                            : form.billingDetailsDirectly?.additionalPriceAdjustmentValue || ''
                        }
                        onChange={(e) => handleAdditionalPriceAdjustmentValue(e)}
                        onBlur={(e) => handleAdditionalPriceAdjustmentValueBlur(e)}
                        className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm w-20"
                      />
                      <span className="text-sm text-muted-foreground">%</span>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="quick_add_modal_form_row errors">
                {depthErrors &&
                  depthErrors.length > 0 &&
                  depthErrors.map((err: string) => {
                    return <span className="shown_error">{err}</span>;
                  })}
              </div>

              <div className="quick_add_modal_actions_container">
                <div className="quick_add_modal_actions">
                  <div>
                    <button className="cancel_quick_add" type="button" onClick={handleCancel}>
                      Cancel
                    </button>
                  </div>
                  <div>
                    <button className="primary_btn" type="button" onClick={handleAdd}>
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div onClick={handleCancel} className={`quick_add_overlay ${active ? 'active' : ''}`} />
    </>
  );
};

export default QuickAddClient;
